import { useEffect, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useTimer } from 'react-timer-hook'

import { Button } from 'components/Button/Button'
import { addToast } from 'components/Toast/Toast'
import { RadioButton } from 'components-ref/atoms'
import { type UserProfile } from 'models'

import './_verificationCode.scss'
import {
  resendLoginTwoFactor,
  verifyLoginTwoFactor
} from './Verification'

type TwoFactorType = 'qr_authenticator' | 'sms'

type VerificationModelProps = {
  challenge: string
  twoFactorType: TwoFactorType
  showBack?: boolean
  onBack?: () => void
  onSuccess: (token: string, userProfile: UserProfile) => void
}

type VerificationCode = {
  digit1: string
  digit2: string
  digit3: string
  digit4: string
  digit5: string
  digit6: string
}

export default function VerificationModel({
  challenge,
  twoFactorType,
  showBack = false,
  onBack,
  onSuccess
}: VerificationModelProps) {
  const { t } = useTranslation()

  const [rememberDevice, setRememberDevice] = useState(false)
  const [sending, setSending] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [codeSent, setCodeSent] = useState(false)
  const hasFetchedOtp = useRef(false)

  const isAppAuthenticator = twoFactorType === 'qr_authenticator'
  const isSmsAuthenticator = twoFactorType === 'sms'

  const [code, setCode] = useState<VerificationCode>({
    digit1: '',
    digit2: '',
    digit3: '',
    digit4: '',
    digit5: '',
    digit6: ''
  })

  const dig1Ref = useRef<HTMLInputElement>(null)
  const dig2Ref = useRef<HTMLInputElement>(null)
  const dig3Ref = useRef<HTMLInputElement>(null)
  const dig4Ref = useRef<HTMLInputElement>(null)
  const dig5Ref = useRef<HTMLInputElement>(null)
  const dig6Ref = useRef<HTMLInputElement>(null)

  const { seconds, minutes, restart } = useTimer({
    expiryTimestamp: new Date(),
    onExpire: () => setCodeSent(false)
  })

  const inputValue = Object.values(code).join('')
  const codeFilled = inputValue.length === 6

  const formatTime = (min: number, sec: number) =>
    `${String(min).padStart(2, '0')}:${String(sec).padStart(2, '0')}`

  const updateCode = (
    key: keyof VerificationCode,
    value: string,
    next?: React.RefObject<HTMLInputElement>
  ) => {
    if (!/^\d?$/.test(value)) return

    setCode(prev => ({
      ...prev,
      [key]: value
    }))

    if (value && next) {
      next.current?.focus()
    }
  }

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
    currentDigit: keyof VerificationCode,
    previousRef: React.RefObject<HTMLInputElement> | null
  ) => {
    if (
      event.key === 'Backspace' &&
      code[currentDigit] === '' &&
      previousRef?.current
    ) {
      previousRef.current.focus()
    }
  }

  const inputBlocks = [
    {
      id: 1,
      value: code.digit1,
      changeFn: (value: string) => updateCode('digit1', value, dig2Ref),
      ref: dig1Ref,
      prevRef: null
    },
    {
      id: 2,
      value: code.digit2,
      changeFn: (value: string) => updateCode('digit2', value, dig3Ref),
      ref: dig2Ref,
      prevRef: dig1Ref
    },
    {
      id: 3,
      value: code.digit3,
      changeFn: (value: string) => updateCode('digit3', value, dig4Ref),
      ref: dig3Ref,
      prevRef: dig2Ref
    },
    {
      id: 4,
      value: code.digit4,
      changeFn: (value: string) => updateCode('digit4', value, dig5Ref),
      ref: dig4Ref,
      prevRef: dig3Ref
    },
    {
      id: 5,
      value: code.digit5,
      changeFn: (value: string) => updateCode('digit5', value, dig6Ref),
      ref: dig5Ref,
      prevRef: dig4Ref
    },
    {
      id: 6,
      value: code.digit6,
      changeFn: (value: string) => updateCode('digit6', value),
      ref: dig6Ref,
      prevRef: dig5Ref
    }
  ]

  const startSmsTimer = () => {
    const newTime = new Date()
    newTime.setMinutes(newTime.getMinutes() + 5)
    restart(newTime)
    setCodeSent(true)
  }

  const sendSmsCode = async () => {
    if (!isSmsAuthenticator || sending) return

    setSending(true)

    try {
      const response = await resendLoginTwoFactor({
        challenge
      })

      if (!response?.success) {
        addToast(
          'error',
          t(response?.message || 'An error occurred while sending OTP')
        )
        return
      }

      startSmsTimer()
      addToast('success', t('OTP sent successfully'))
    } catch (error) {
      const message =
        (error as any)?.response?.data?.message ??
        (error as any)?.response?.data?.error?.message ??
        t('An error occurred while sending OTP')

      addToast('error', t(message))
    } finally {
      setSending(false)
    }
  }

  useEffect(() => {
    if (!isSmsAuthenticator || hasFetchedOtp.current) return

    hasFetchedOtp.current = true
    void sendSmsCode()
  }, [isSmsAuthenticator])

  const submitOtp = async () => {
    if (!codeFilled || submitting) return

    setSubmitting(true)

    try {
      const response = await verifyLoginTwoFactor({
        challenge,
        otp: inputValue,
        remember_device: rememberDevice
      })

      if (!response?.token || !response?.userProfile) {
        addToast(
          'error',
          t(response?.message || 'OTP verification failed')
        )
        return
      }

      addToast('success', t('OTP verified successfully'))

      onSuccess(
        response.token,
        response.userProfile
      )
    } catch (error) {
      const message =
        (error as any)?.response?.data?.message ??
        (error as any)?.response?.data?.error?.message ??
        t('An unexpected error occurred')

      addToast('error', t(message))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className='verification-box'>
      <hr />

      <h2 className='verification-title'>
        {t('Verification Code')}
      </h2>

      <div className='verificationCode__input'>
        {inputBlocks.map(block => (
          <input
            key={block.id}
            ref={block.ref}
            type='text'
            inputMode='numeric'
            autoComplete='one-time-code'
            value={block.value}
            maxLength={1}
            onChange={event => block.changeFn(event.currentTarget.value)}
            onKeyDown={event =>
              handleKeyDown(
                event,
                `digit${block.id}` as keyof VerificationCode,
                block.prevRef
              )
            }
            className={`verificationCode__input__block${
              block.value
                ? ' verificationCode__input__block--filled'
                : ''
            }`}
          />
        ))}
      </div>

      <div className='verification-description'>
        <p className='verification-description'>
          {isAppAuthenticator
            ? t('Enter the code from your authenticator app.')
            : t('Enter the verification code sent to your phone.')}
        </p>
      </div>

      {isSmsAuthenticator && (
        <div style={{ textAlign: 'center' }}>
          {codeSent ? (
            <div className='verificationCode__actions__codesent'>
              {`Didn’t get the OTP?`}
              <br />
              {t('reSendSms')} in{' '}
              <span>{formatTime(minutes, seconds)}</span>
            </div>
          ) : (
            <Button
              text={t('Resend Code')}
              color='black'
              font='mont-14-normal'
              width='13'
              height='3'
              disabled={sending}
              clickFn={() => {
                void sendSmsCode()
              }}
              customClass='verificationCode__actions__button'
            />
          )}
        </div>
      )}

      <div className='checkbox-group'>
        <RadioButton
          active={rememberDevice}
          clickFn={() => setRememberDevice(current => !current)}
        />
        <label htmlFor='rememberDevice'>
          Remember this device for 30 days
        </label>
      </div>

      <div
        className='button-group'
        style={{
          display: 'flex',
          gap: 12,
          marginTop: 12
        }}
      >
        {showBack && (
          <Button
            text={t('back')}
            color='transparent'
            font='mont-14-normal'
            width='20'
            height='5'
            clickFn={() => onBack?.()}
            customClass='verification-back-btn'
          />
        )}

        <Button
          text={t('submit')}
          color='blue'
          font='mont-14-normal'
          width='20'
          height='5'
          disabled={!codeFilled || submitting}
          clickFn={() => {
            void submitOtp()
          }}
          customClass='submit-btn'
        />
      </div>
    </div>
  )
}